import { Box, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { tabTitleFor } from './EditorTabBar'
import { ContentFile } from '../../../services/api'
import { EditorTab } from '../hooks/useEditorTabs'

interface TabSwitcherProps {
  enabled: boolean
  tabs: EditorTab[]
  chapters: ContentFile[]
  /** Open-tab filenames in most-recently-used order (from useEditorTabs). */
  getMruOrder: () => string[]
  direction: 'rtl' | 'ltr'
  titleFontFamily?: string
  onSwitch: (filename: string) => void
}

/**
 * WebStorm-style Ctrl+Tab switcher bound to ⌥Tab (the browser reserves
 * Ctrl+Tab). Hold ⌥ and tap Tab to cycle through the open tabs in
 * most-recently-used order (⇧ reverses); releasing ⌥ switches to the selected
 * tab, so a quick ⌥Tab tap toggles between the two most recent files. Arrows
 * also move the selection, Escape cancels, clicking a row switches.
 *
 * Deliberately self-contained: the open/cycle state lives here so every
 * keystroke re-renders only this small popup — never the whole BookEditor
 * tree, which is what made the first version feel sluggish.
 */
export default function TabSwitcher({
  enabled,
  tabs,
  chapters,
  getMruOrder,
  direction,
  titleFontFamily,
  onSwitch,
}: TabSwitcherProps) {
  const [open, setOpen] = useState(false)
  const [filenames, setFilenames] = useState<string[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)

  // Key events can arrive faster than React re-renders (key repeat, a quick
  // tap-and-release) — the refs are the authoritative state, mirrored to
  // useState only for rendering.
  const tabsRef = useRef(tabs)
  tabsRef.current = tabs
  const openRef = useRef(false)
  const filenamesRef = useRef<string[]>([])
  const selectedIndexRef = useRef(0)
  const onSwitchRef = useRef(onSwitch)
  onSwitchRef.current = onSwitch
  const getMruOrderRef = useRef(getMruOrder)
  getMruOrderRef.current = getMruOrder

  const cancel = useCallback(() => {
    openRef.current = false
    setOpen(false)
  }, [])

  const commit = useCallback((index?: number) => {
    if (!openRef.current) {
      return
    }
    openRef.current = false
    setOpen(false)
    const target = filenamesRef.current[index ?? selectedIndexRef.current]
    if (target) {
      onSwitchRef.current(target)
    }
  }, [])

  const select = useCallback((index: number) => {
    selectedIndexRef.current = index
    setSelectedIndex(index)
  }, [])

  useEffect(() => {
    if (!enabled) {
      return
    }

    const moveSelection = (delta: number) => {
      const len = filenamesRef.current.length
      select((selectedIndexRef.current + delta + len) % len)
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && !e.metaKey && !e.ctrlKey && e.key === 'Tab') {
        if (!openRef.current) {
          // Don't steal the shortcut while a dialog/menu overlay is up —
          // switching the underlying chapter beneath it would be confusing.
          if (tabsRef.current.length < 2 || document.querySelector('.MuiModal-root')) {
            return
          }
          e.preventDefault()
          e.stopPropagation()
          const openTabs = tabsRef.current
          const mru = getMruOrderRef.current().filter((f) => openTabs.some((t) => t.filename === f))
          const rest = openTabs.map((t) => t.filename).filter((f) => !mru.includes(f))
          const list = [...mru, ...rest]
          filenamesRef.current = list
          setFilenames(list)
          select(e.shiftKey ? list.length - 1 : 1)
          openRef.current = true
          setOpen(true)
          return
        }
        e.preventDefault()
        e.stopPropagation()
        moveSelection(e.shiftKey ? -1 : 1)
        return
      }

      if (!openRef.current) {
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        e.stopPropagation()
        moveSelection(e.key === 'ArrowDown' ? 1 : -1)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        commit()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        cancel()
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      if (openRef.current && e.key === 'Alt') {
        commit()
      }
    }

    const handleWindowBlur = () => {
      cancel()
    }

    window.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('keyup', handleKeyUp, true)
    window.addEventListener('blur', handleWindowBlur)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('keyup', handleKeyUp, true)
      window.removeEventListener('blur', handleWindowBlur)
    }
  }, [enabled, select, commit, cancel])

  const items = useMemo(
    () => filenames.map((filename) => ({ filename, title: tabTitleFor(chapters, filename) })),
    [filenames, chapters],
  )

  const selectedRowRef = useRef<HTMLDivElement | null>(null)

  // Keep the selected row visible while cycling through a long list.
  useEffect(() => {
    selectedRowRef.current?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex, open])

  if (!open) {
    return null
  }

  return (
    <Box
      dir={direction}
      sx={{
        position: 'fixed',
        top: 110,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: (theme) => theme.zIndex.modal + 1,
        width: 380,
        maxWidth: 'calc(100vw - 48px)',
        bgcolor: 'var(--bg-surface)',
        border: '1px solid var(--toolbar-page-border)',
        borderRadius: 2,
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.25)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ maxHeight: 320, overflowY: 'auto', py: 0.5 }}>
        {items.map((item, index) => {
          const isSelected = index === selectedIndex
          return (
            <Box
              key={item.filename}
              ref={isSelected ? selectedRowRef : undefined}
              onMouseEnter={() => select(index)}
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault()
                commit(index)
              }}
              sx={{
                px: 1.75,
                py: 0.75,
                cursor: 'pointer',
                bgcolor: isSelected ? 'var(--bg-hover)' : 'transparent',
                borderInlineStart: isSelected ? '3px solid var(--sidebar-heading-active)' : '3px solid transparent',
              }}
            >
              <Typography
                component="span"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontFamily: titleFontFamily || 'inherit',
                  fontSize: 14,
                  fontWeight: isSelected ? 600 : 400,
                  color: 'var(--text-primary)',
                }}
                title={item.title}
              >
                {item.title}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
